import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "../../../store";
import {
  postCreateAccount,
  postInviteAccount,
  CreateAccountRequest,
  CreateAccountResponse,
  InviteAccountRequest,
  InviteAccountResponse,
} from "../../../api/awsAccountsStore";
import { Toast } from "shared-ui/src/components/Toast";

interface AwsAccountsState {
  createAccountStatus: "idle" | "loading" | "success" | "error";
  inviteAccountStatus: "idle" | "loading" | "success" | "error";
  lastCreateResponse: CreateAccountResponse | null;
  lastInviteResponse: InviteAccountResponse | null;
  error: string | null;
}

const initialState: AwsAccountsState = {
  createAccountStatus: "idle",
  inviteAccountStatus: "idle",
  lastCreateResponse: null,
  lastInviteResponse: null,
  error: null,
};

const awsAccountsSlice = createSlice({
  name: "awsAccounts",
  initialState,
  reducers: {
    createAccountStart(state) {
      state.createAccountStatus = "loading";
      state.error = null;
    },
    createAccountSuccess(state, action: PayloadAction<CreateAccountResponse>) {
      state.createAccountStatus = "success";
      state.lastCreateResponse = action.payload;
    },
    createAccountError(state, action: PayloadAction<string>) {
      state.createAccountStatus = "error";
      state.error = action.payload;
    },
    inviteAccountStart(state) {
      state.inviteAccountStatus = "loading";
      state.error = null;
    },
    inviteAccountSuccess(state, action: PayloadAction<InviteAccountResponse>) {
      state.inviteAccountStatus = "success";
      state.lastInviteResponse = action.payload;
    },
    inviteAccountError(state, action: PayloadAction<string>) {
      state.inviteAccountStatus = "error";
      state.error = action.payload;
    },
    resetStatus(state) {
      state.createAccountStatus = "idle";
      state.inviteAccountStatus = "idle";
      state.error = null;
    },
  },
});

export const {
  createAccountStart,
  createAccountSuccess,
  createAccountError,
  inviteAccountStart,
  inviteAccountSuccess,
  inviteAccountError,
  resetStatus,
} = awsAccountsSlice.actions;

// Thunks
export const requestCreateAccount =
  (request: CreateAccountRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(createAccountStart());
      const response = await postCreateAccount(request);
      dispatch(createAccountSuccess(response));
      Toast("AWS account creation request submitted successfully! You will receive an email notification when the account is created.");
    } catch (error: any) {
      const errorMessage = error.message || "Failed to create account";
      dispatch(createAccountError(errorMessage));
      Toast(`Failed to submit AWS account creation request: ${errorMessage}`, true);
    }
  };

export const requestInviteAccount =
  (request: InviteAccountRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(inviteAccountStart());
      const response = await postInviteAccount(request);
      dispatch(inviteAccountSuccess(response));
      Toast("AWS account invitation request submitted successfully! You will receive an email notification when the invitation is sent.");
    } catch (error: any) {
      const errorMessage = error.message || "Failed to invite account";
      dispatch(inviteAccountError(errorMessage));
      Toast(`Failed to submit AWS account invitation request: ${errorMessage}`, true);
    }
  };

export default awsAccountsSlice.reducer;
